/**
 * Safe localStorage wrapper that handles errors gracefully
 * Prevents crashes in private/incognito mode or when storage is disabled
 */

class SafeStorage {
  private isAvailable: boolean;

  constructor() {
    this.isAvailable = this.checkAvailability();
  }

  private checkAvailability(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn('localStorage is not available:', e);
      return false;
    }
  }

  /**
   * Safely get an item from localStorage
   * @param key - The key to retrieve
   * @param defaultValue - Default value if item doesn't exist or error occurs
   * @returns The stored value or default value
   */
  getItem<T = string>(key: string, defaultValue: T | null = null): T | null {
    if (!this.isAvailable) {
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      return item !== null ? (item as unknown as T) : defaultValue;
    } catch (e) {
      console.error(`Error reading from localStorage (key: ${key}):`, e);
      return defaultValue;
    }
  }

  /**
   * Safely get and parse JSON from localStorage
   * @param key - The key to retrieve
   * @param defaultValue - Default value if item doesn't exist or error occurs
   * @returns The parsed object or default value
   */
  getJSON<T>(key: string, defaultValue: T | null = null): T | null {
    if (!this.isAvailable) {
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (e) {
      console.error(`Error parsing JSON from localStorage (key: ${key}):`, e);
      return defaultValue;
    }
  }

  /**
   * Safely set an item in localStorage
   * @param key - The key to set
   * @param value - The value to store
   * @returns true if successful, false otherwise
   */
  setItem(key: string, value: string): boolean {
    if (!this.isAvailable) {
      return false;
    }

    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.error(`Error writing to localStorage (key: ${key}):`, e);
      return false;
    }
  }

  /**
   * Safely set an object as JSON in localStorage
   * @param key - The key to set
   * @param value - The object to store
   * @returns true if successful, false otherwise
   */
  setJSON<T>(key: string, value: T): boolean {
    if (!this.isAvailable) {
      return false;
    }

    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
      return true;
    } catch (e) {
      console.error(`Error writing JSON to localStorage (key: ${key}):`, e);
      return false;
    }
  }

  /**
   * Safely remove an item from localStorage
   * @param key - The key to remove
   * @returns true if successful, false otherwise
   */
  removeItem(key: string): boolean {
    if (!this.isAvailable) {
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.error(`Error removing from localStorage (key: ${key}):`, e);
      return false;
    }
  }

  /**
   * Safely clear all items from localStorage
   * @returns true if successful, false otherwise
   */
  clear(): boolean {
    if (!this.isAvailable) {
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch (e) {
      console.error('Error clearing localStorage:', e);
      return false;
    }
  }

  /**
   * Check if localStorage is available
   * @returns true if localStorage is available, false otherwise
   */
  isStorageAvailable(): boolean {
    return this.isAvailable;
  }
}

// Export singleton instance
export const safeStorage = new SafeStorage();

// Also export the class for testing purposes
export default SafeStorage;
